import React from 'react';
import { ArrowRightIcon, UsersIcon } from 'lucide-react';
import { PayeeSummary } from '../../types/commission';
import { payeeStoredTotal } from '../../utils/commission';
import { formatUsd, sumCents } from '../../utils/format';
import { TL_UNIT_COUNT_REVIEW_NOTE } from '../../data/periodClose';
import { Badge } from '../sf/Badge';
import { NewBadge } from '../sf/NewBadge';
import { Tooltip } from '../sf/Tooltip';
import { Card } from '../sf/Card';
import { Td, Th } from '../sf/DataTable';
import { ExampleDataBadge } from '../sf/ExampleDataBadge';
import { Money } from '../sf/Money';
import { StoredReference } from '../sf/StoredReference';
import { UnderReviewBadge } from '../sf/UnderReviewBadge';
interface PayeeTableProps {
  payees: PayeeSummary[];
  onViewDashboard: () => void;
}
const dash = <span className="text-sf-weak" data-id="element-700" data-mp="1ruf19qpf2p9j">—</span>;
export function PayeeTable({
  payees,
  onViewDashboard
}: PayeeTableProps) {
  const sum = (pick: (payee: PayeeSummary) => number) => sumCents(payees.map(pick));
  const totals = {
    units: payees.reduce((count, payee) => count + payee.units, 0),
    volume: sum(p => p.volume),
    base: sum(p => p.base),
    bonus: sum(p => p.bonus),
    overrides: sum(p => p.overridesAdjusted),
    manual: sum(p => p.manual),
    stored: sum(payeeStoredTotal),
    adjusted: sum(p => p.adjustedTotal)
  };
  return <Card title={`Payouts by payee (${payees.length})`} icon={UsersIcon} iconBg="#0176D3" meta="Each total is the sum of that payee’s commission lines. Adjusted pays; Stored (unadjusted) is reference only." bodyClassName="" actions={<ExampleDataBadge data-id="element-702" data-mp="1io8bid8edltu" />} data-id="element-701" data-mp="1rh1w8q6m5df3">
      <div className="overflow-x-auto" data-id="element-703" data-mp="1mqbstihqzkn1">
        <table className="w-full border-collapse" data-id="element-704" data-mp="1mpa5aejjp5of">
          <thead data-id="element-705" data-mp="1mnz1ieq3nxxb">
            <tr data-id="element-706" data-mp="2ag62jr6rst32">
              <Th data-id="element-707" data-mp="2afnw61qe8yhf">Payee</Th>
              <Th data-id="element-708" data-mp="2aecx2f7w8ewn">Branch</Th>
              <Th align="right" data-id="element-709" data-mp="2ae31mc42xfjk">Units</Th>
              <Th align="right" data-id="element-710" data-mp="2f6wbfeeene2i">Volume</Th>
              <Th align="right" className="font-normal" data-id="element-711" data-mp="2f8qu0m27eu3f">
                Base
                <span className="block text-[11px] text-sf-weak" data-id="element-712" data-mp="2p235aevov2hl">stored ref.</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-713" data-mp="2l1j6l6vgvqtc">
                Bonus
                <span className="block text-[11px] text-sf-weak" data-id="element-714" data-mp="2g0vk8yi6ny5g">stored ref.</span>
              </Th>
              <Th align="right" data-id="element-715" data-mp="2b1sf7e27drxh">
                TL / Director
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-716" data-mp="263ihavp3gd86">adjusted</span>
              </Th>
              <Th align="right" data-id="element-717" data-mp="21ciljtrpo179">Manual & Adj.</Th>
              <Th align="right" data-id="element-718" data-mp="21br1ng7h7rwt">
                Adjusted
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-719" data-mp="1xanm6utcbxw4">adj. loan × BPS · pays</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-720" data-mp="1sdngwm4bez62">
                Stored (unadjusted)
                <span className="block text-[11px] text-sf-weak" data-id="element-721" data-mp="1nby2cwhvw3d8">reference</span>
              </Th>
              <Th data-id="element-722" data-mp="1ifhbvvn7pcwz">
                <span className="sr-only" data-id="element-723" data-mp="1iey4v9z3o8xq">Dashboard</span>
              </Th>
            </tr>
          </thead>
          <tbody data-id="element-724" data-mp="18cc0z5ewz9lv">
            {payees.map(payee => <tr key={payee.id} data-id="element-725" data-mp="1d8aybii801yf">
                <Td className="whitespace-nowrap" data-id="element-726" data-mp="1dbf8qxma3txj">
                  <span className="flex items-center gap-2" data-id="element-727" data-mp="1i85mheam2dfs">
                    <span className="font-semibold" data-id="element-728" data-mp="1i6kv1xjlfam5">{payee.name}</span>
                    {payee.role !== 'Loan Officer' && <Badge tone="info" data-id="element-729" data-mp="pjrp90rcjl5a">{payee.role}</Badge>}
                  </span>
                  {payee.note && <span className="flex items-center gap-1.5 text-xs text-sf-weak" data-id="element-730" data-mp="uibdj7tex90t">
                      {payee.note}
                      {payee.noteIsNew && <NewBadge label="NEW" data-id="element-731" data-mp="zgcwztur3act" />}
                    </span>}
                  {payee.bonusNote && <span className="mt-0.5 block max-w-[320px] whitespace-normal text-[11px] text-sf-weak" data-id="element-732" data-mp="14aqty0p29pjl">{payee.bonusNote}</span>}
                  {payee.role !== 'Loan Officer' && <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-sf-weak" data-id="element-733" data-mp="bqs70r06jcu1">
                      Bonus: none on TL / Director row
                      <Tooltip content={TL_UNIT_COUNT_REVIEW_NOTE} align="start" data-id="element-734" data-mp="28ixyhd98bo6c">
                        <UnderReviewBadge data-id="element-735" data-mp="2n7c05kuwt6lb" />
                      </Tooltip>
                    </span>}
                </Td>
                <Td className="whitespace-nowrap text-sf-weak" data-id="element-736" data-mp="2ifkgzve32357">{payee.branch}</Td>
                <Td align="right" data-id="element-737" data-mp="2w78tw0w5ovn8">{payee.units || dash}</Td>
                <Td align="right" data-id="element-738" data-mp="2w9v11zf4x94x">{payee.volume ? formatUsd(payee.volume) : dash}</Td>
                <Td align="right" data-id="element-739" data-mp="2s65bb6mq4lib">{payee.base ? <StoredReference value={payee.base} compact data-id="element-740" data-mp="365y18zqfexp8" /> : dash}</Td>
                <Td align="right" data-id="element-741" data-mp="3643ow9t80dwr">{payee.bonus ? <StoredReference value={payee.bonus} compact data-id="element-742" data-mp="317d2l4qtom2w" /> : dash}</Td>
                <Td align="right" data-id="element-743" data-mp="315aeiq98d30t">
                  {payee.overridesAdjusted ? <>
                      <Money value={payee.overridesAdjusted} data-id="element-744" data-mp="16t2dcv6bfpwl" />
                      <span className="block" data-id="element-745" data-mp="16uwwq51xv9yk">
                        <StoredReference value={payee.overrides} compact data-id="element-746" data-mp="3ifw1q76p9rcw" />
                      </span>
                    </> : dash}
                </Td>
                <Td align="right" data-id="element-747" data-mp="38re4u11jk4ej">{payee.manual ? <Money value={payee.manual} signed data-id="element-748" data-mp="38q2zvxwd0qlu" /> : dash}</Td>
                <Td align="right" className="font-bold" data-id="element-749" data-mp="3doxiayqf0d5f">
                  <Money value={payee.adjustedTotal} data-id="element-750" data-mp="3dncq3frkp6aq" />
                </Td>
                <Td align="right" data-id="element-751" data-mp="2zomrfvp3uyk9">
                  <StoredReference value={payeeStoredTotal(payee)} compact data-id="element-752" data-mp="34p11tke6iklg" />
                </Td>
                <Td data-id="element-753" data-mp="2prrw1lo4pea7">
                  <button type="button" onClick={onViewDashboard} className="inline-flex items-center gap-1 whitespace-nowrap rounded text-[13px] text-sf-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-754" data-mp="2psa2fb4i98vu">
                    Dashboard <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-755" data-mp="syozk8wwc3hp" />
                  </button>
                </Td>
              </tr>)}
            <tr className="bg-[#FAFAF9] font-semibold" data-id="element-756" data-mp="miyjwtdz9la2">
              <Td colSpan={2} data-id="element-757" data-mp="mlawss4vp5g9">Period total</Td>
              <Td align="right" data-id="element-758" data-mp="hjdb7r909yk5">{totals.units}</Td>
              <Td align="right" data-id="element-759" data-mp="hm7vnis0rjdk">{formatUsd(totals.volume)}</Td>
              <Td align="right" data-id="element-760" data-mp="djuz9kz5wxtg">
                <StoredReference value={totals.base} compact data-id="element-761" data-mp="dkmhlu3r0z1s" />
              </Td>
              <Td align="right" data-id="element-762" data-mp="1a80z7ul5321e">
                <StoredReference value={totals.bonus} compact data-id="element-763" data-mp="159po7gj3ylhl" />
              </Td>
              <Td align="right" data-id="element-764" data-mp="15bsmenuezkxe">
                <Money value={totals.overrides} data-id="element-765" data-mp="10iheejdv8y3w" />
              </Td>
              <Td align="right" data-id="element-766" data-mp="10gmsb1quyzy8">
                <Money value={totals.manual} signed data-id="element-767" data-mp="whxkdoolaalz" />
              </Td>
              <Td align="right" className="text-[14px] font-bold" data-id="element-768" data-mp="1sza9i62qhuu1">
                <Money value={totals.adjusted} data-id="element-769" data-mp="1t0v764kchtw4" />
              </Td>
              <Td align="right" data-id="element-770" data-mp="1o8boit6sw8kg">
                <StoredReference value={totals.stored} compact data-id="element-771" data-mp="1o7aems1its4b" />
              </Td>
              <Td data-id="element-772" data-mp="36wmyx4sxu4h7" />
            </tr>
          </tbody>
        </table>
      </div>
    </Card>;
}